import { useRefereeListQuery, UseRefereeListQueryParams } from '../queries';
import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { Input, SearchSelectProps } from '~/components/Input';
import { BaseFormState } from '~/types/BaseFormState';

type Props<TFormState extends BaseFormState> = Omit<
  SearchSelectProps<TFormState>,
  'options' | 'search' | 'setSearch' | 'isLoading'
> & {
  query?: Omit<Exclude<UseRefereeListQueryParams, undefined>, 'search' | 'page_size'>;
};

function RefereeSearchSelectElement<TFormState extends BaseFormState>(
  props: Props<TFormState>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const [search, setSearch] = useState<string>('');

  const { data, isLoading } = useRefereeListQuery(
    {
      ...props.query,
      page_size: 100,
      search,
    },
    search.length > 3
  );

  const options = useMemo<[string, number][]>(() => {
    return data?.results.map(({ id, person }) => [`${person.first_name} ${person.last_name}`, id]) ?? [];
  }, [data]);

  return (
    <Input.SearchSelect
      setSearch={setSearch}
      isLoading={isLoading}
      options={options}
      search={search}
      ref={ref}
      {...props}
    />
  );
}

export const RefereeSearchSelect = forwardRef(RefereeSearchSelectElement) as <TFormState extends BaseFormState>(
  props: Props<TFormState> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof RefereeSearchSelectElement>;
